import React from 'react';
import ReactMarkdown from 'react-markdown';
import TableRenderer from './TableRenderer';
import { Callout } from '@blueprintjs/core';
import remarkGfm from 'remark-gfm';
import clsx from 'clsx';
import { CodeProps } from 'react-markdown/lib/ast-to-react';
import ResourceAssetFileRenderer from './ResourceAssetFileRenderer';
import { extractDimensionsFromStyle } from './utils';
import { utf8ToBase64, base64ToUtf8 } from '../../apiHooks/utils';

interface IMarkdownProps {
    linksTargetSelf?: boolean;
    string: string;
    className?: string;
    resourceVersionUuid?: string;
}

interface IState {
    renderErrorMessage: string | null;
}

function transformIframesToMarkdownImage(input: string): string {
    if (!input) return input;

    return input.replace(/<iframe\b[^>]*>[\s\S]*?<\/iframe>/gi, (tag) => {
        try {
            const parser = new DOMParser();
            const doc = parser.parseFromString(tag, 'text/html');
            const iframe = doc.querySelector('iframe');
            if (!iframe) return tag;

            const src = iframe.getAttribute('src') || '';
            const title = iframe.getAttribute('title') || '';
            const width = iframe.getAttribute('width') || '';
            const height = iframe.getAttribute('height') || '';
            const style = iframe.getAttribute('style') || '';

            if (!src) return tag;

            const styleDimensions = extractDimensionsFromStyle(style);
            const finalWidth = styleDimensions.width || width || '';
            const finalHeight = styleDimensions.height || height || '';

            const properties = { src, title, width: finalWidth, height: finalHeight };
            const encodedProperties = utf8ToBase64(JSON.stringify(properties));

            return `![${title}](iframe://#${encodedProperties})`;
        } catch (error) {
            console.error('Error parsing iframe tag:', error);
            return tag;
        }
    });
}

function transformImgsToMarkdownImage(input: string): string {
    if (!input) return input;

    return input.replace(/<img\b[^>]*\/?>/gi, (tag) => {
        try {
            const parser = new DOMParser();
            const doc = parser.parseFromString(tag, 'text/html');
            const img = doc.querySelector('img');
            if (!img) return tag;

            const src = img.getAttribute('src') || '';
            const alt = img.getAttribute('alt') || '';
            const width = img.getAttribute('width') || '';
            const height = img.getAttribute('height') || '';
            const style = img.getAttribute('style') || '';

            if (!src) return tag;

            const styleDimensions = extractDimensionsFromStyle(style);
            const finalWidth = styleDimensions.width || width || '';
            const finalHeight = styleDimensions.height || height || '';

            if (!finalWidth && !finalHeight) return tag;

            const properties = { src, alt, width: finalWidth, height: finalHeight };
            const encodedProperties = utf8ToBase64(JSON.stringify(properties));

            return `![${alt}](img://#${encodedProperties})`;
        } catch (error) {
            console.error('Error parsing img tag:', error);
            return tag;
        }
    });
}

function transformFiguresToMarkdownImage(input: string): string {
    if (!input) return input;

    return input.replace(/<figure\b[^>]*>[\s\S]*?<\/figure>/gi, (tag) => {
        try {
            const parser = new DOMParser();
            const doc = parser.parseFromString(tag, 'text/html');
            const figure = doc.querySelector('figure');
            if (!figure) return tag;

            const img = figure.querySelector('img');
            const figcaption = figure.querySelector('figcaption');

            if (!img) return tag;

            const src = img.getAttribute('src') || '';
            const alt = img.getAttribute('alt') || '';
            const width = img.getAttribute('width') || '';
            const height = img.getAttribute('height') || '';
            const style = img.getAttribute('style') || '';
            const caption = figcaption?.textContent || '';

            if (!src) return tag;

            const styleDimensions = extractDimensionsFromStyle(style);
            const finalWidth = styleDimensions.width || width || '';
            const finalHeight = styleDimensions.height || height || '';

            const properties = { src, alt, width: finalWidth, height: finalHeight, caption };
            const encodedProperties = utf8ToBase64(JSON.stringify(properties));

            return `![${alt}](figure://#${encodedProperties})`;
        } catch (error) {
            console.error('Error parsing figure tag:', error);
            return tag;
        }
    });
}

// Matches fenced code blocks (group 1 is the fence, group 2 its character), inline code spans (group 3 is the backtick run)
// and HTML code/pre elements (group 5 is the tag name), which are left unmasked so their backticks are kept
const markdownCodeRegex =
    /^ {0,3}((`|~)\2{2,})[^\n]*\n[\s\S]*?(?:^ {0,3}\1\2*[ \t]*\r?$|(?![\s\S]))|(`+)((?:(?!\n[ \t]*\r?\n)[\s\S])*?[^`])\3(?!`)|<(code|pre)\b[^>]*>[\s\S]*?<\/\5>/gim;

function transformOutsideMarkdownCode(input: string, transform: (input: string) => string): string {
    if (!input) return input;

    const codeSnippets: string[] = [];
    const inputWithPlaceholders = input.replace(markdownCodeRegex, (code) => {
        if (code.startsWith('<')) return code;
        codeSnippets.push(code);
        return `\uE000${codeSnippets.length - 1}\uE000`;
    });

    return transform(inputWithPlaceholders).replace(/\uE000(\d+)\uE000/g, (_, index) => codeSnippets[Number(index)]);
}

function toMarkdownInlineCode(code: string): string {
    const longestBacktickRun = Math.max(0, ...(code.match(/`+/g) ?? []).map((run) => run.length));
    const fence = '`'.repeat(longestBacktickRun + 1);
    return `${fence} ${code} ${fence}`;
}

function toMarkdownInline(node: Node): string {
    if (node.nodeType === Node.TEXT_NODE) return (node.textContent ?? '').replace(/[\\`*_[\]<~]/g, '\\$&');
    if (node.nodeType !== Node.ELEMENT_NODE) return '';

    const element = node as Element;
    const children = Array.from(element.childNodes).map(toMarkdownInline).join('');
    switch (element.nodeName) {
        case 'CODE':
            return toMarkdownInlineCode(element.textContent ?? '');
        case 'A': {
            const href = element.getAttribute('href');
            return href ? `[${children}](<${href}>)` : children;
        }
        case 'STRONG':
        case 'B':
            return `**${children}**`;
        case 'EM':
        case 'I':
            return `*${children}*`;
        default:
            return children;
    }
}

function transformTablesToMarkdownTable(input: string): string {
    if (!input) return input;

    return input.replace(/<table\b[^>]*>[\s\S]*?<\/table>/gi, (tag) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(tag, 'text/html');
        const rows = Array.from(doc.querySelectorAll('tr')).map((row) =>
            Array.from(row.querySelectorAll('th, td')).map((cell) =>
                toMarkdownInline(cell).replace(/\s+/g, ' ').trim().replace(/\|/g, '\\|'),
            ),
        );

        const columnCount = Math.max(0, ...rows.map((row) => row.length));
        if (columnCount === 0) return tag;

        const toMarkdownRow = (cells: string[]) =>
            `| ${Array.from({ length: columnCount }, (_, index) => cells[index] ?? '').join(' | ')} |`;
        const [headerRow, ...bodyRows] = rows;

        return [
            '',
            toMarkdownRow(headerRow),
            toMarkdownRow(Array(columnCount).fill('---')),
            ...bodyRows.map(toMarkdownRow),
            '',
        ].join('\n');
    });
}

function transformPreToMarkdownCodeBlock(input: string): string {
    if (!input) return input;

    return input.replace(/<pre\b[^>]*>[\s\S]*?<\/pre>/gi, (tag) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(tag, 'text/html');
        const code = doc.querySelector('pre')?.textContent?.replace(/\n$/, '');
        if (!code) return tag;

        const longestBacktickRun = Math.max(0, ...(code.match(/`+/g) ?? []).map((run) => run.length));
        const fence = '`'.repeat(Math.max(3, longestBacktickRun + 1));
        return `\n${fence}\n${code}\n${fence}\n`;
    });
}

function transformCodeToMarkdownCode(input: string): string {
    if (!input) return input;

    return input.replace(/<code\b[^>]*>[\s\S]*?<\/code>/gi, (tag) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(tag, 'text/html');
        const code = doc.querySelector('code')?.textContent?.replace(/\s+/g, ' ');
        if (!code) return tag;

        return toMarkdownInlineCode(code);
    });
}

export default class RenderMarkdown extends React.Component<IMarkdownProps, IState> {
    public state = {
        renderErrorMessage: null,
    };

    public componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        this.setState({ renderErrorMessage: error.message });
    }

    public render() {
        const { renderErrorMessage } = this.state;
        if (renderErrorMessage !== null) {
            return (
                <Callout intent='danger' title='Failed to render Markdown'>
                    {renderErrorMessage}
                </Callout>
            );
        }

        const preprocessed = [
            transformIframesToMarkdownImage,
            transformFiguresToMarkdownImage,
            transformImgsToMarkdownImage,
            transformTablesToMarkdownTable,
            transformPreToMarkdownCodeBlock,
            transformCodeToMarkdownCode,
        ].reduce((markdown, transform) => transformOutsideMarkdownCode(markdown, transform), this.props.string);

        return (
            <ReactMarkdown
                className={clsx('prose max-w-none print:prose-sm w-full', this.props.className)}
                children={preprocessed}
                disallowedElements={['linkReference', 'imageReference']}
                remarkPlugins={[remarkGfm]}
                components={{
                    img: GetImageRender(this.props.resourceVersionUuid),
                    a: LinkRender(!!this.props.linksTargetSelf),
                    table: TableRenderer,
                    code: CodeRenderer,
                }}
            />
        );
    }
}

const GetImageRender = (resourceVersionUuid?: string) => (props: { src?: string; alt?: string }) => {
    const src = props.src ?? '';

    const iframePrefix = 'iframe://';
    if (src.startsWith(iframePrefix)) {
        if (!resourceVersionUuid) {
            console.error('No resourceVersionUuid provided for markdown iframe rendering');
            return <pre>Failed to render iframe "{props.alt}"</pre>;
        }

        const urlWithoutPrefix = src.slice(iframePrefix.length);
        const [, encodedProperties] = urlWithoutPrefix.split('#');

        try {
            const decodedProperties = JSON.parse(base64ToUtf8(encodedProperties)) as {
                src?: string;
                height?: string;
                width?: string;
                title?: string;
            };
            if (!decodedProperties.src) {
                console.error('No src provided for markdown iframe rendering');
                return <pre>Failed to render iframe "{props.alt}"</pre>;
            }

            return (
                <ResourceAssetFileRenderer
                    src={decodedProperties.src}
                    resourceVersionUuid={resourceVersionUuid}
                    width={decodedProperties.width}
                    height={decodedProperties.height}
                />
            );
        } catch (error) {
            console.error('Error decoding iframe properties:', error);
            return <pre>Failed to render iframe "{props.alt}"</pre>;
        }
    }

    const imgPrefix = 'img://';
    if (src.startsWith(imgPrefix)) {
        const urlWithoutPrefix = src.slice(imgPrefix.length);
        const [, encodedProperties] = urlWithoutPrefix.split('#');

        try {
            const decodedProperties = JSON.parse(base64ToUtf8(encodedProperties)) as {
                src?: string;
                height?: string;
                width?: string;
                alt?: string;
            };
            if (!decodedProperties.src) {
                console.error('No src provided for markdown img rendering');
                return <pre>Failed to render img "{props.alt}"</pre>;
            }

            const assetPrefix = 'assets://';
            if (decodedProperties.src.startsWith(assetPrefix)) {
                if (!resourceVersionUuid) {
                    console.error('No resourceVersionUuid provided for markdown img rendering');
                    return <pre>Failed to render img "{props.alt}"</pre>;
                }

                return (
                    <ResourceAssetFileRenderer
                        src={decodedProperties.src}
                        resourceVersionUuid={resourceVersionUuid}
                        width={decodedProperties.width}
                        height={decodedProperties.height}
                        alt={decodedProperties.alt || props.alt}
                    />
                );
            }

            console.error('Only assets:// URLs are supported for markdown img rendering');
            return <pre>Failed to render img "{props.alt}" - only assets:// URLs supported</pre>;
        } catch (error) {
            console.error('Error decoding img properties:', error);
            return <pre>Failed to render img "{props.alt}"</pre>;
        }
    }

    const figurePrefix = 'figure://';
    if (src.startsWith(figurePrefix)) {
        const urlWithoutPrefix = src.slice(figurePrefix.length);
        const [, encodedProperties] = urlWithoutPrefix.split('#');

        try {
            const decodedProperties = JSON.parse(base64ToUtf8(encodedProperties)) as {
                src?: string;
                height?: string;
                width?: string;
                alt?: string;
                caption?: string;
            };
            if (!decodedProperties.src) {
                console.error('No src provided for markdown figure rendering');
                return <pre>Failed to render figure "{props.alt}"</pre>;
            }

            const assetPrefix = 'assets://';
            if (decodedProperties.src.startsWith(assetPrefix)) {
                if (!resourceVersionUuid) {
                    console.error('No resourceVersionUuid provided for markdown figure rendering');
                    return <pre>Failed to render figure "{props.alt}"</pre>;
                }

                return (
                    <ResourceAssetFileRenderer
                        src={decodedProperties.src}
                        resourceVersionUuid={resourceVersionUuid}
                        width={decodedProperties.width}
                        height={decodedProperties.height}
                        alt={decodedProperties.alt || props.alt}
                        figureCaption={decodedProperties.caption}
                        isFigure={true}
                    />
                );
            }

            console.error('Only assets:// URLs are supported for markdown figure rendering');
            return <pre>Failed to render figure "{props.alt}" - only assets:// URLs supported</pre>;
        } catch (error) {
            console.error('Error decoding figure properties:', error);
            return <pre>Failed to render figure "{props.alt}"</pre>;
        }
    }

    const assetPrefix = 'assets://';
    if (src.startsWith(assetPrefix)) {
        if (!resourceVersionUuid) {
            console.error('No resourceVersionUuid provided for markdown asset image rendering');
            return <pre>Failed to render image "{props.alt}"</pre>;
        }

        return <ResourceAssetFileRenderer src={src} resourceVersionUuid={resourceVersionUuid} alt={props.alt} />;
    }

    const [objectUrlFileHash, base64Filename] = src.split(';');
    if (objectUrlFileHash && base64Filename) {
        return <img src={`blob:${location.origin}/${objectUrlFileHash}`} alt={props.alt} crossOrigin='anonymous' />;
    }
    return <img src={props.src} alt={props.alt} crossOrigin='anonymous' />;
};

const LinkRender =
    (linksTargetSelf: boolean): React.FC<{ href?: string; children?: any }> =>
    (props) => {
        const href = props.href ?? '';
        const [objectUrlFileHash, base64Filename] = href.split(';');
        if (objectUrlFileHash && base64Filename) {
            const filename = base64ToUtf8(base64Filename);
            return (
                <a href={`blob:${location.origin}/${objectUrlFileHash}`} download={filename}>
                    {props.children}
                </a>
            );
        }
        return (
            <a href={props.href} rel='nofollow noreferrer' target={linksTargetSelf ? '_self' : '_blank'}>
                {props.children}
            </a>
        );
    };

function CodeRenderer(props: CodeProps) {
    return <code className={clsx({ 'whitespace-pre-wrap': props.inline })}>{props.children}</code>;
}
